import React, { useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Environment, Stats } from '@react-three/drei';
import GameLevel from './game/GameLevel';
import UI from './ui/UI';
import ResponsiveContainer from './components/ResponsiveContainer';
import TouchControls from './components/TouchControls';
import { useResponsive } from './hooks/useResponsive';
import { DrawingCanvasProvider } from './components/DrawingCanvasManager';
import { JokeInputProvider } from './components/JokeInputManager';
import { Vector3 } from 'three';

// Movement component to handle WASD controls
const MovementControls: React.FC = () => {
  const controlsRef = useRef<any>(null);
  const { camera } = useThree();
  const moveState = useRef({
    forward: false,
    backward: false,
    left: false,
    right: false
  });

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key.toLowerCase()) {
        case 'w':
          moveState.current.forward = true;
          break;
        case 's':
          moveState.current.backward = true;
          break;
        case 'a':
          moveState.current.left = true;
          break;
        case 'd':
          moveState.current.right = true;
          break;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      switch (e.key.toLowerCase()) {
        case 'w':
          moveState.current.forward = false;
          break;
        case 's':
          moveState.current.backward = false;
          break;
        case 'a':
          moveState.current.left = false;
          break;
        case 'd':
          moveState.current.right = false;
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useFrame(() => {
    if (!controlsRef.current) return;

    const speed = 0.5;
    const moveVector = new Vector3();

    if (moveState.current.forward) moveVector.z -= speed;
    if (moveState.current.backward) moveVector.z += speed;
    if (moveState.current.left) moveVector.x -= speed;
    if (moveState.current.right) moveVector.x += speed;

    if (moveVector.length() > 0) {
      moveVector.applyQuaternion(camera.quaternion);
      camera.position.add(moveVector);
      controlsRef.current.target.add(moveVector);
      controlsRef.current.update();
    }
  });

  return (
    <OrbitControls
      ref={controlsRef}
      makeDefault
      enableDamping
      dampingFactor={0.05}
      minDistance={5}
      maxDistance={50}
      maxPolarAngle={Math.PI / 2}
      minPolarAngle={0}
      enablePan={true}
      panSpeed={0.5}
      enableZoom={true}
      zoomSpeed={0.5}
      enableRotate={true}
      rotateSpeed={0.5}
      mouseButtons={{
        LEFT: undefined, // Disable left click for orbit
        MIDDLE: undefined, // Disable middle click
        RIGHT: 2, // Use right click for orbit
      }}
    />
  );
};

const GameContainer: React.FC = () => {
  const { isMobile, isTablet, scaleFactor } = useResponsive();
  
  // Check for WebGL support
  const hasWebGL = (() => {
    try {
      const canvas = document.createElement('canvas');
      return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
    } catch (e) {
      return false;
    }
  })();

  if (!hasWebGL) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: '#1e293b',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        textAlign: 'center',
        padding: '20px'
      }}>
        <h2>WebGL Not Supported</h2>
        <p>Your device does not support WebGL, which is required to run this game.</p>
        <p>Please try using a different browser or device.</p>
      </div>
    );
  }
  
  return (
    <DrawingCanvasProvider>
      <JokeInputProvider>
        <ResponsiveContainer>
          <Canvas
            shadows
            camera={{ position: [0, 15, 15], fov: 50 }}
            style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(to bottom, #1e293b, #334155)',
              touchAction: 'none' // Prevent default touch actions
            }}
            gl={{
              antialias: true,
              alpha: true,
              preserveDrawingBuffer: true
            }}
          >
            <Stats />
            <MovementControls />
            <Environment preset="city" />
            <ambientLight intensity={0.5} />
            <directionalLight
              position={[10, 10, 10]}
              intensity={1}
              castShadow
              shadow-mapSize-width={2048}
              shadow-mapSize-height={2048}
              shadow-camera-far={50}
              shadow-camera-left={-20}
              shadow-camera-right={20}
              shadow-camera-top={20}
              shadow-camera-bottom={-20}
            />
            
            {/* Game components */}
            <GameLevel />
          </Canvas>
          
          {/* UI Overlays */}
          <UI />
          <TouchControls />
        </ResponsiveContainer>
      </JokeInputProvider>
    </DrawingCanvasProvider>
  );
};

export default GameContainer; 